import { getFormatDateFromObject, getDateFromString } from 'shared-components';
import { PassportDocs, PassportList, ICourse } from '../../../../types';
import ChecklistSection from '../../../atoms/checklist-section';
import { isPassportExpired, isPassportExpiringSoon } from '../../../../utilities/validation';
import Finger, { Status } from '../../../atoms/finger';
import { getVisible, getDocStatus } from '../../../../utilities/documents';
import { useIsSectionLocked } from '../../../../hooks/useIsSectionLocked';
import { SectionId } from '../../../molecules/Sidebar/types';
import { useSelector } from 'react-redux';
import { ApplicationState } from '../../../../store';

interface IProps {
  infoOnly: boolean;
  documents: PassportDocs;
  passportStatus: string;
  passportExpiryDate: string;
  passportExpectedDate: string;
  warning?: string;
  courses: Array<ICourse>;
  onResume: (url: string) => void;
  onClick: (docType: 'current' | 'renew', docKey: string, infoOnly: boolean) => void;
}

const Passport = (props: IProps) => {
  const { documents: passports, infoOnly } = props;
  const offer = useSelector((state: ApplicationState) => state.student.offer);
  const isSectionLocked = useIsSectionLocked(SectionId.GENERAL_DOC, offer.status);

  const { passportStatus, passportExpectedDate, passportExpiryDate, courses } = props;
  let { warning } = props;

  const currentDocs = getVisible(passports.current ?? {}, infoOnly) as PassportList[];

  const renewDocs = getVisible(passports.renew ?? {}, infoOnly) as PassportList[];

  if (currentDocs.length === 0 && renewDocs.length === 0) return null;

  const passportInvalidForCourse = passportStatus === 'Yes' && isPassportExpiringSoon(courses, passportExpiryDate);

  if (!warning) {
    if (passportStatus === 'Yes' && isPassportExpired(passportExpiryDate)) {
      warning = 'Your passport has expired, please renew it before you upload';
    } else if (passportInvalidForCourse) {
      warning = 'Your passport expires soon, we recommend you renew it as soon as possible';
    } else if (passportStatus !== 'Yes') {
      warning = `Expected date ${passportExpectedDate}`;
    }
  }

  return (
    <ChecklistSection
      label={`Please upload a scan of your passport ID page`}
      warning={warning}
      edit={() => {
        props.onResume('personal-info/step-7');
      }}
    >
      {currentDocs.map((passport: PassportList, index: number) => {
        const { key: docKey, item } = passport;

        const passportLabel = `Current passport ${index > 0 ? index + 1 : ''} ${
          item.dateOfExpiry ? `- expiry ${getFormatDateFromObject(getDateFromString(item.dateOfExpiry))}` : ''
        }`;
        if (item.status === 'REJECTED') {
          return (
            <Finger
              hideButton={true}
              reason={item.reason}
              key={`passport-${docKey}`}
              id={`passport-${docKey}`}
              status={Status.Rejected}
              label={passportLabel}
            />
          );
        } else {
          return (
            <Finger
              key={`passport-${docKey}`}
              id={`passport-${docKey}`}
              status={getDocStatus(item.status, infoOnly, isSectionLocked)}
              completeLabel={infoOnly ? 'View' : undefined}
              resolveLabel={infoOnly ? 'Please Confirm' : undefined}
              buttonLabel={infoOnly ? 'Please review' : undefined}
              label={passportLabel}
              onClick={() => props.onClick('current', docKey, infoOnly)}
            />
          );
        }
      })}
      {renewDocs.map((passport: PassportList, index: number) => {
        const { key: docKey, item } = passport;

        const passportLabel = `New/renewed passport ${index > 0 ? index + 1 : ''}`;

        if (item.status === 'REJECTED') {
          return (
            <Finger
              hideButton={true}
              reason={item.reason}
              key={`passport-renew-${docKey}`}
              id={`passport-renew-${docKey}`}
              status={Status.Rejected}
              label={passportLabel}
            />
          );
        } else {
          return (
            <Finger
              key={`passport-renew--${docKey}`}
              id={`passport-renew-${docKey}`}
              status={getDocStatus(item.status, infoOnly, isSectionLocked)}
              completeLabel={infoOnly ? 'View' : undefined}
              resolveLabel={infoOnly ? 'Please Confirm' : undefined}
              buttonLabel={infoOnly ? 'Please review' : undefined}
              label={passportLabel}
              onClick={() => props.onClick('renew', docKey, infoOnly)}
            />
          );
        }
      })}
    </ChecklistSection>
  );
};

export default Passport;
